import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SavedCardType } from '../../types';
import { apiService } from '../../services/apiService';
import { patchCachedTechCard } from '../techcard/api';
import { tcTrack } from '../techcard/track';
import { fy, type ForYouState, type KeywordRow, type Problem, type Rec, type SavedTech, type TechMini } from './api';
import { useForYouStyles } from './styles';
import { fyTrack } from './track';
import ProblemsSection, { type EditMode } from './ProblemsSection';
import RecommendedSection from './RecommendedSection';
import SavedSection from './SavedSection';
import ShareDialog from './ShareDialog';
import InterestsSection from './InterestsSection';

/**
 * For You (spec 24 Oct 2026; layout from the prototype): problem statements →
 * recommended for the selected one → saved technologies (select + share) and
 * interests with this week's featured technology, side by side from 900 px.
 * Everything comes from GET /api/v1/foryou/state (Backend/foryou.py); every
 * mutation answers with the new state. Cards open the app's tech modal
 * (onReadMore). Bookmarks stay in step with the rest of the app through the
 * `utech:saved-changed` window event, both ways.
 */

export interface ForYouPageProps {
  onReadMore: (type: SavedCardType, id: string, relevance?: number) => void;
  /** Kept for the old page's call site; not used. */
  interestsData?: unknown;
  setActiveView?: (view: string) => void;
  onNavigateToProfileTab?: (tab: string) => void;
}

const SAVED_EVENT = 'utech:saved-changed';

export const ForYouPage: React.FC<ForYouPageProps> = ({ onReadMore }) => {
  useForYouStyles();
  const [st, setSt] = useState<ForYouState | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [activeId, setActiveId] = useState<number | null>(null);
  const [mode, setMode] = useState<EditMode>('view');
  const [pBusy, setPBusy] = useState(false);
  const [pErr, setPErr] = useState('');
  const [kBusy, setKBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [shareOpen, setShareOpen] = useState(false);
  // Picked in "Recommended for this problem": shared through the same dialog.
  const [recSel, setRecSel] = useState<Set<string>>(new Set());
  const [recShare, setRecShare] = useState<Rec[] | null>(null);
  const [toast, setToast] = useState('');
  const toastT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewed = useRef(false);
  const ownSavedEvent = useRef(0);

  const showToast = useCallback((m: string) => {
    setToast(m);
    if (toastT.current) clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(''), 3200);
  }, []);
  useEffect(() => () => { if (toastT.current) clearTimeout(toastT.current); }, []);

  const apply = useCallback((s: ForYouState) => {
    setSt(s);
    setActiveId(cur => (cur !== null && s.problems.some(p => p.id === cur)) ? cur : (s.problems[0]?.id ?? null));
    // A selection of something no longer saved is dropped.
    setSelected(sel => {
      const ok = new Set(s.saved.filter(t => t.available).map(t => t.id));
      const next = new Set([...sel].filter(id => ok.has(id)));
      return next.size === sel.size ? sel : next;
    });
  }, []);

  const load = useCallback(async () => {
    const r = await fy.state();
    if (r.ok && r.data) { apply(r.data); setLoadErr(''); } else if (!st) setLoadErr(r.error);
    return r;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apply]);

  useEffect(() => {
    load().then(r => {
      if (r.ok && r.data && !viewed.current) {
        viewed.current = true;
        fyTrack('foryou_view', { problems: r.data.problems.length, keywords: r.data.keywords.length,
                                 saved: r.data.saved.length });
      }
    });
  }, [load]);

  // Matching runs in the background: while a statement is pending, look again.
  const pending = !!st?.problems.some(p => p.status === 'pending') || !!st?.keywords.some(k => k.state === 'pending');
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => { if (document.visibilityState === 'visible') void load(); }, 20000);
    return () => clearInterval(t);
  }, [pending, load]);

  // Bookmarks made elsewhere (the tech modal, Cosmos, Saved) refresh this page.
  useEffect(() => {
    const on = () => {
      if (Date.now() - ownSavedEvent.current < 800) return;
      void fy.saved().then(r => {
        if (r.ok && r.data) setSt(s => (s ? { ...s, saved: r.data!.items } : s));
      });
    };
    window.addEventListener(SAVED_EVENT, on);
    return () => window.removeEventListener(SAVED_EVENT, on);
  }, []);

  const savedIds = useMemo(() => new Set((st?.saved || []).map(t => t.id)), [st?.saved]);
  const active: Problem | null = st?.problems.find(p => p.id === activeId) || null;
  const sections = st?.settings.sections || { problems: true, recommended: true, saved: true, interests: true };

  // ---- problems ------------------------------------------------------------
  const saveProblem = async (text: string): Promise<boolean> => {
    setPBusy(true); setPErr('');
    const isNew = mode === 'new';
    const r = isNew ? await fy.addProblem(text) : await fy.editProblem(active!.id, text);
    setPBusy(false);
    if (!r.ok || !r.data) { setPErr(r.error); return false; }
    apply(r.data);
    if (isNew) {
      const added = (r.data as ForYouState & { added?: number }).added;
      if (added) setActiveId(added);
      fyTrack('foryou_problem_add', { position: r.data.problems.length, chars: text.length });
      showToast('Problem statement added — finding matches');
    } else {
      fyTrack('foryou_problem_edit', { position: active?.position, chars: text.length });
      showToast('Problem statement updated — refreshing matches');
    }
    setMode('view');
    return true;
  };
  const removeProblem = async () => {
    if (!active) return;
    setPBusy(true); setPErr('');
    const pos = active.position;
    const r = await fy.removeProblem(active.id);
    setPBusy(false);
    if (!r.ok || !r.data) { setPErr(r.error); return; }
    fyTrack('foryou_problem_remove', { position: pos });
    setActiveId(r.data.problems[0]?.id ?? null);
    apply(r.data);
    showToast('Problem statement removed');
  };

  // ---- bookmarks + opening techs --------------------------------------------
  const setSavedLocal = (t: TechMini, on: boolean) => {
    setSt(s => {
      if (!s) return s;
      if (on) {
        if (s.saved.some(x => x.id === t.id)) return s;
        const row: SavedTech = { ...t, asset_id: t.id, saved_at: new Date().toISOString() };
        return { ...s, saved: [row, ...s.saved] };
      }
      return { ...s, saved: s.saved.filter(x => x.id !== t.id) };
    });
  };
  // `assetId`: the id the save is stored under, when unsaving from the Saved
  // list — a stored long id would not match the short one and the unsave
  // would silently miss.
  const bookmark = async (r: TechMini & { relevance?: number }, on: boolean, surface: string, assetId?: string) => {
    const uid = apiService.getStoredUserId();
    if (!uid) return;
    setSavedLocal(r, on);
    let ok = false;
    try {
      const res = await apiService.saveCard({ asset_type: 'technology', asset_id: assetId || r.id, bookmarked: on, liked: null,
                                              linked_chat_id: null }, uid);
      ok = !!res.success;
    } catch { ok = false; }
    if (!ok) { setSavedLocal(r, !on); showToast('Could not update your bookmark — please try again.'); return; }
    fyTrack('foryou_rec_bookmark', { tech_id: r.id, on, surface, relevance: r.relevance });
    tcTrack('techcard_bookmark', { on, tech_id: r.id, surface });
    patchCachedTechCard(r.id, { bookmarked: on } as any);
    ownSavedEvent.current = Date.now();
    window.dispatchEvent(new Event(SAVED_EVENT));
    showToast(on ? 'Saved to your technologies' : 'Removed from saved');
    if (!on) setSelected(sel => { const n = new Set(sel); n.delete(r.id); return n; });
    // The server's copy (saved time, tags) replaces the optimistic row.
    void fy.saved().then(x => { if (x.ok && x.data) setSt(s => (s ? { ...s, saved: x.data!.items } : s)); });
  };
  const openRec = (r: Rec, rank: number, surface: string) => {
    fyTrack('foryou_rec_open', { tech_id: r.id, rank, relevance: r.relevance, match: r.match, surface,
                                 problem_position: active?.position });
    onReadMore(SavedCardType.Tech, r.id);
  };
  const openSaved = (t: SavedTech) => {
    fyTrack('foryou_saved_open', { tech_id: t.id, surface: 'foryou_saved' });
    onReadMore(SavedCardType.Tech, t.id);
  };
  const openFeatured = (k: KeywordRow, t: TechMini) => {
    fyTrack('foryou_featured_open', { tech_id: t.id, keyword: k.keyword, week: k.week_key, surface: 'foryou_featured' });
    onReadMore(SavedCardType.Tech, t.id);
  };

  // ---- keywords --------------------------------------------------------------
  const addKeyword = async (keyword: string): Promise<string> => {
    setKBusy(true);
    const r = await fy.addKeyword(keyword);
    setKBusy(false);
    if (!r.ok || !r.data) return r.error;
    apply(r.data);
    const added = (r.data as ForYouState & { added?: string }).added || keyword;
    fyTrack('foryou_keyword_add', { keyword: added, count: r.data.keywords.length });
    showToast(r.data.keywords.find(k => k.keyword === added)?.state === 'ok'
      ? `${added} added — your featured tech offer is ready`
      : `${added} added — finding your featured tech offer`);
    return '';
  };
  const removeKeyword = async (k: KeywordRow) => {
    setKBusy(true);
    const r = await fy.removeKeyword(k.keyword);
    setKBusy(false);
    if (!r.ok || !r.data) { showToast(r.error); return; }
    apply(r.data);
    fyTrack('foryou_keyword_remove', { keyword: k.keyword, count: r.data.keywords.length });
    showToast(`${k.keyword} removed from your interests`);
  };

  // ---- share -------------------------------------------------------------------
  const savedShare = (st?.saved || []).filter(t => t.available && selected.has(t.id));
  const shareItems: TechMini[] = recShare ?? savedShare;
  const openShare = () => {
    if (!shareItems.length) return;
    fyTrack('foryou_share_open', { count: shareItems.length });
    setShareOpen(true);
  };
  const openRecShare = (items: Rec[]) => {
    if (!items.length) return;
    fyTrack('foryou_share_open', { count: items.length, from: 'recommended' });
    setRecShare(items);
    setShareOpen(true);
  };
  const closeShare = () => { setShareOpen(false); setRecShare(null); };
  const unpick = (id: string) => {
    if (recShare) {
      const left = recShare.filter(r => r.id !== id);
      setRecSel(sel => { const n = new Set(sel); n.delete(id); return n; });
      if (left.length) setRecShare(left); else closeShare();
      return;
    }
    const next = new Set(selected); next.delete(id);
    setSelected(next);
    if (![...next].some(x => savedIds.has(x))) setShareOpen(false);
  };
  useEffect(() => { setRecSel(new Set()); }, [activeId]);

  // ---- render --------------------------------------------------------------------
  if (!st) {
    return (
      <main className="fyx flex flex-col flex-1 self-stretch rounded-2xl bg-white shadow-custom-light p-4 sm:p-6 gap-6 overflow-y-auto hide-scrollbar">
        <div className="fy-page">
          <header>
            <div className="fy-kicker">For You</div>
            <h1 className="fy-h1">Technologies matched to your problems and interests</h1>
          </header>
          {loadErr ? (
            <div className="fy-sec">
              <div className="fy-err">{loadErr}</div>
              <div><button type="button" className="fy-btn fy-btn-outline" onClick={() => { setLoadErr(''); void load(); }}>Try again</button></div>
            </div>
          ) : (
            <>
              <div className="fy-skel" style={{ minHeight: 180 }} />
              <div className="fy-grid">{[0, 1, 2, 3].map(i => <div key={i} className="fy-skel" />)}</div>
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="fyx flex flex-col flex-1 self-stretch rounded-2xl bg-white shadow-custom-light p-4 sm:p-6 gap-6 overflow-y-auto hide-scrollbar">
      <div className="fy-page">
        <header>
          <div className="fy-kicker">For You</div>
          <h1 className="fy-h1">Technologies matched to your problems and interests</h1>
        </header>

        {sections.problems && (
          <ProblemsSection problems={st.problems} settings={st.settings} active={active}
                           onSelect={p => { fyTrack('foryou_problem_select', { position: p.position }); setActiveId(p.id); }}
                           mode={mode} setMode={m => { setPErr(''); setMode(m); }}
                           onSave={saveProblem} onRemove={removeProblem} busy={pBusy} error={pErr} />
        )}

        {sections.problems && sections.recommended && active && mode === 'view' && (
          <RecommendedSection problem={active} topShown={st.settings.top_shown} savedIds={savedIds}
                              onOpen={openRec} onBookmark={bookmark} onEdit={() => setMode('edit')}
                              selected={recSel} setSelected={setRecSel} onShare={openRecShare} />
        )}

        {(sections.saved || sections.interests) && (
          <div className="fy-two">
            {sections.saved && (
              <SavedSection items={st.saved} selected={selected} setSelected={setSelected}
                            onOpen={openSaved} onShare={openShare}
                            onUnsave={t => { void bookmark(t, false, 'foryou_saved', t.asset_id); }} />
            )}
            {sections.interests && (
              <InterestsSection keywords={st.keywords} max={st.settings.max_keywords} weekLabel={st.week.label}
                                onAdd={addKeyword} onRemove={removeKeyword} onOpen={openFeatured} busy={kBusy} />
            )}
          </div>
        )}
      </div>

      {shareOpen && shareItems.length > 0 && (
        <ShareDialog items={shareItems} maxRecipients={st.settings.share_max_recipients}
                     noteMax={st.settings.share_note_max} onUnpick={unpick}
                     onClose={closeShare}
                     onSent={m => { if (recShare) setRecSel(new Set()); else setSelected(new Set()); closeShare(); showToast(m); }}
                     onCopied={m => { closeShare(); showToast(m); }} />
      )}
      {toast && <div className="fy-toast" role="status">{toast}</div>}
    </main>
  );
};

export default ForYouPage;
